/**
 * Wall-clock date/time conversion, shared by the `DateTimePicker` component and
 * any feature that needs to edit an instant as a date plus a time.
 *
 * These deliberately read and write in the **viewer's local timezone**. Calls
 * store `scheduledAt` as a bare ISO instant with no timezone field (unlike
 * calendar events, which carry one), so local is the only reading available and
 * it matches what the native `datetime-local` input did before this existed.
 */

/**
 * A date and time with no timezone attached, which is what a picker actually
 * manipulates. `month` is 1-12 rather than the `Date` constructor's 0-11 so
 * callers reading a literal in isolation cannot get it silently wrong.
 */
export interface LocalDateTimeParts {
  year: number;
  /** 1-12. */
  month: number;
  day: number;
  /** 0-23. */
  hour24: number;
  minute: number;
}

/**
 * Split an ISO instant into local wall-clock parts for a picker to edit.
 * Null in, null out; an unparseable string is also null rather than a bogus
 * 1970 date that would look like a real selection.
 */
export const isoToLocalParts = (
  iso: string | null
): LocalDateTimeParts | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour24: date.getHours(),
    minute: date.getMinutes(),
  };
};

/**
 * Rebuild an ISO instant from local wall-clock parts.
 *
 * Goes through the multi-argument `Date` constructor deliberately: it interprets
 * its arguments in the local zone and resolves DST for us. On a spring-forward
 * day a nonexistent wall-clock time (2:30am where 2am jumps straight to 3am)
 * lands on the next real instant instead of throwing, which is the same
 * forgiving behavior the native input had.
 */
export const localPartsToIso = (parts: LocalDateTimeParts): string | null => {
  const { year, month, day, hour24, minute } = parts;
  const date = new Date(year, month - 1, day, hour24, minute, 0, 0);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
};

/**
 * Cached `Intl.DateTimeFormat`s.
 *
 * Constructing one costs 10-100x what calling `.format()` on an existing one does, and these are
 * built inside render paths that run per calendar event and per review row. Measured on the bulk
 * review list: a single keystroke at 60 rows spent ~26 ms purely constructing formatters, which is
 * past the frame budget on its own — with the cache, ~2 ms.
 *
 * Bounded in practice by the handful of distinct option sets the app uses.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

export const dateTimeFormatter = (
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat => {
  const key = `${locale ?? ""}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    formatterCache.set(key, formatter);
  }
  return formatter;
};

/**
 * Compact format for a trigger sitting in a table row, e.g. "Aug 1, 6:00 PM".
 *
 * The long form ("Aug 1, 2026 at 6:00 PM") wraps to two lines in a ~180px column and, because
 * builder rows are fixed-height for the virtualizer, a wrapped trigger spills over the row
 * below. The year is included only when it is not the current one, since a call is almost
 * always near-term and the year is noise.
 */
export const formatLocalPartsShort = (
  parts: LocalDateTimeParts,
  locale?: string,
  now: Date = new Date()
): string => {
  const iso = localPartsToIso(parts);
  if (!iso) return "";
  const date = new Date(iso);
  const day = dateTimeFormatter(locale, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  }).format(date);
  const time = dateTimeFormatter(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${day}, ${time}`;
};

/** Format local parts for a picker trigger, e.g. "Aug 1, 2026 at 6:00 PM". */
export const formatLocalParts = (
  parts: LocalDateTimeParts,
  locale?: string
): string => {
  const iso = localPartsToIso(parts);
  if (!iso) return "";
  const date = new Date(iso);
  const day = dateTimeFormatter(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
  const time = dateTimeFormatter(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${day} at ${time}`;
};

/** Days in a 1-12 month, leap years included. Day 0 of the next month is this month's last. */
export const daysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

/** Weekday index (0 = Sunday) that a 1-12 month starts on. */
export const firstWeekdayOfMonth = (year: number, month: number): number =>
  new Date(year, month - 1, 1).getDay();

/**
 * Shift a calendar day by `delta` days, rolling across month and year
 * boundaries. Time parts ride along untouched.
 */
export const addDays = (
  parts: LocalDateTimeParts,
  delta: number
): LocalDateTimeParts => {
  const date = new Date(parts.year, parts.month - 1, parts.day + delta);
  return {
    ...parts,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

/**
 * Shift by whole months, clamping the day so Jan 31 going forward lands on
 * Feb 28 rather than rolling into March.
 */
export const addMonths = (
  parts: LocalDateTimeParts,
  delta: number
): LocalDateTimeParts => {
  const target = new Date(parts.year, parts.month - 1 + delta, 1);
  const year = target.getFullYear();
  const month = target.getMonth() + 1;
  return {
    ...parts,
    year,
    month,
    day: Math.min(parts.day, daysInMonth(year, month)),
  };
};

export const isSameDay = (
  a: LocalDateTimeParts | null,
  b: LocalDateTimeParts | null
): boolean =>
  !!a && !!b && a.year === b.year && a.month === b.month && a.day === b.day;

/** Local parts for right now, with seconds discarded. */
export const nowLocalParts = (): LocalDateTimeParts => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour24: now.getHours(),
    minute: now.getMinutes(),
  };
};
